import { cssInput, textInput } from "app/client/ui/inputs";
import { theme } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";

import { Computed, dom, DomElementArg, IDisposableOwner, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-vpw-input-");

export class InvalidPasswordError extends Error {}

/**
 * Regexes for Cognito's password requirements. Cognito only allows ASCII letters,
 * numbers, and some special characters (including inner spaces).
 *
 * Reference: https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html.
 */
const LOWERCASE_LETTER = /[a-z]/;
const NUMBER = /\d/;
const OUTER_WHITESPACE = /(^\s+)|(\s+$)/;
const INVALID_CHARACTERS = /[^a-zA-Z\d^$*.[{}()?"!@#%&/,><':;|_~`=+ \]\\-]/;

/**
 * Builds a password input that validates against a set of password
 * constraints, displaying them below the input. Sets `isValid` to true
 * if `password` is not empty, and passes all constraints.
 *
 * Constraints that pass are displayed with a green tick, and constraints
 * that don't pass are displayed with a red cross. While the input has focus,
 * constraints that haven't yet passed are generally displayed in gray, only
 * changing to red when focus leaves the input. The current exception to this
 * is the invalid characters constraint, which is flagged immediately to warn
 * users that their password contains an invalid (i.e. non-ASCII) character.
 *
 * Currently checks for password length, use of lowercase letters and numbers,
 * and the presence of any invalid characters.
 */
export function buildValidatedPasswordInput(
  owner: IDisposableOwner,
  password: Observable<string>,
  isValid: Observable<boolean>,
  onCreate: (input: HTMLElement) => void,
  ...args: DomElementArg[]
) {
  const hasPassword = Computed.create(owner, password, (_use, pw) => pw.length > 0);
  const isMinLengthValid = Computed.create(owner, password, (_use, pw) => pw.length >= 8);
  const isMaxLengthValid = Computed.create(owner, password, (_use, pw) => pw.length <= 256);
  const hasLowercaseLetter = Computed.create(owner, password, (_use, pw) => LOWERCASE_LETTER.test(pw));
  const hasNumber = Computed.create(owner, password, (_use, pw) => NUMBER.test(pw));
  const hasNoInvalidCharacters = Computed.create(owner, password, (_use, pw) => !INVALID_CHARACTERS.test(pw));
  const hasNoOuterWhitespace = Computed.create(owner, password, (_use, pw) => !OUTER_WHITESPACE.test(pw));

  // False while password is being edited, so that validation errors aren't displayed excessively.
  const shouldHighlightUnmetConstraintsAsErrors = Observable.create(owner, false);
  owner.autoDispose(password.addListener(() => shouldHighlightUnmetConstraintsAsErrors.set(false)));

  const isPasswordValid = Computed.create(owner, use =>
    use(isMinLengthValid) &&
    use(isMaxLengthValid) &&
    use(hasLowercaseLetter) &&
    use(hasNumber) &&
    use(hasNoInvalidCharacters) &&
    use(hasNoOuterWhitespace),
  );

  // True if the password input should be outlined in red, to indicate validation errors.
  const shouldHighlightErrors = Computed.create(owner, use =>
    use(password).length > 0 && !use(isPasswordValid) &&
    (use(shouldHighlightUnmetConstraintsAsErrors) || !use(hasNoInvalidCharacters) || !use(hasNoOuterWhitespace)),
  );

  owner.autoDispose(isPasswordValid.addListener(isPassValid => isValid.set(isPassValid)));

  return dom("div",
    textInput(password,
      {
        id: "new-password",
        name: "password",
        placeholder: "Enter password",
        type: "password",
        required: "true",
        autocomplete: "new-password",
      },
      el => onCreate(el),
      dom.on("change", () => shouldHighlightUnmetConstraintsAsErrors.set(true)),
      cssInput.cls("-invalid", shouldHighlightErrors),
      cssInput.cls("-valid", isPasswordValid),
      ...args,
    ),
    dom.maybe(hasPassword, () => cssValidationResults(
      validationConstraint(
        "Password cannot exceed 256 characters",
        {
          isConstraintMet: isMaxLengthValid,
          onlyShowWhenUnmet: true,
        },
      ),
      validationConstraint(
        "Password cannot contain non-English letters",
        {
          isConstraintMet: hasNoInvalidCharacters,
          onlyShowWhenUnmet: true,
        },
      ),
      validationConstraint(
        "Password cannot start or end with a space",
        {
          isConstraintMet: hasNoOuterWhitespace,
          onlyShowWhenUnmet: true,
        },
      ),
      validationConstraint(
        "Password must contain a lowercase letter",
        {
          isConstraintMet: hasLowercaseLetter,
          shouldHighlightErrors: shouldHighlightUnmetConstraintsAsErrors,
        },
      ),
      validationConstraint(
        "Password must contain a number",
        {
          isConstraintMet: hasNumber,
          shouldHighlightErrors: shouldHighlightUnmetConstraintsAsErrors,
        },
      ),
      validationConstraint(
        "Password must contain at least 8 characters",
        {
          isConstraintMet: isMinLengthValid,
          shouldHighlightErrors: shouldHighlightUnmetConstraintsAsErrors,
        },
      ),
    )),
  );
}

interface ConstraintOptions {
  isConstraintMet: Observable<boolean>;
  shouldHighlightErrors?: Observable<boolean>;
  onlyShowWhenUnmet?: boolean;
}

/**
 * Builds a validation icon and message. Used for displaying password constraints.
 *
 * Displays met constraints in green, and unmet constraints in either red or gray,
 * depending on whether `shouldHighlightErrors` is true; always highlights errors if
 * `shouldHighlightErrors` is unset.
 *
 * Set `onlyShowWhenUnmet` to conditionally hide the constraint when met; useful for
 * showing transient validation errors.
 */
function validationConstraint(
  constraintText: string,
  { isConstraintMet, shouldHighlightErrors, onlyShowWhenUnmet }: ConstraintOptions,
) {
  return dom.domComputed((use) => {
    const isMet = use(isConstraintMet);
    if (onlyShowWhenUnmet && isMet) { return null; }

    const shouldHighlightErrs = shouldHighlightErrors ? use(shouldHighlightErrors) : true;
    const unmetIcon = shouldHighlightErrs ? cssRedIcon("CrossSmall") : cssGrayIcon("NewNotification");

    return cssValidationConstraint(
      isMet ? cssGreenIcon("Tick") : unmetIcon,
      cssConstraintText(constraintText),
      cssValidationConstraint.cls(isMet ? "-met" : shouldHighlightErrs ? "-invalid" : "-pending"),
      testId("validation-constraint"),
    );
  });
}

const cssValidationResults = styled("div", `
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  gap: 12px;
`);

const cssValidationConstraint = styled("div", `
  display: flex;
  align-items: flex-start;
  gap: 4px;

  &-invalid {
    color: ${theme.inputInvalid};
  }

  &-met {
    color: ${theme.inputValid};
  }

  &-pending {
    color: ${theme.lightText};
  }
`);

const cssIcon = styled(icon, `
  width: 16px;
  height: 16px;
`);

const cssRedIcon = styled(cssIcon, `
  background-color: ${theme.inputInvalid};
`);

const cssGreenIcon = styled(cssIcon, `
  background-color: ${theme.inputValid};
`);

const cssGrayIcon = styled(cssIcon, `
  background-color: ${theme.lightText};
`);

const cssConstraintText = styled("div", `
  font-weight: 400;
  line-height: 16px;
  font-size: 14px;
`);
